import { Check } from '@mui/icons-material';
import { Fade, Stack, SvgIcon } from '@mui/material';
import { useXaaMethodGuide } from 'api/guide';
import { useCurrentXaaIdP } from 'api/idps';
import IdPChip from 'components/shared/IdPChip';
import SidebarMenuItem from 'components/SidebarMenu/SidebarMenuItem';
import SidebarMenuSection from 'components/SidebarMenu/SidebarMenuSection';
import { ROUTES } from 'constants/routes';
import { GuideType } from 'consts';
import { useXAAGuideProgress } from 'contexts/GuideProgressContext';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import React from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useNav, useStepNavigate } from 'utils/navigation';

interface IXAAMenuSectionProps {
	open: boolean;
	toggleOpen: () => void;
}

// Mirrors SSO/SCIM sidebar sections: a select-idp entry (routing through the XAA select
// page) plus the guide steps, always populated via useXaaMethodGuide (not only when
// inside the XAA flow).
const XAAMenuSection: React.FC<IXAAMenuSectionProps> = ({
	open,
	toggleOpen
}) => {
	const nav = useNav();
	const location = useLocation();

	const { xaaGuideStepNum, guideType } = useIdPConfigContext();

	const { data: currentXaaIdPData } = useCurrentXaaIdP();

	const { data: xaaGuideData } = useXaaMethodGuide();

	const { isStepCompleted, completedSteps, totalSteps } = useXAAGuideProgress();

	const { navigateToXaaStep } = useStepNavigate();

	return (
		<SidebarMenuSection
			title={intl.tr('general.XAAConfiguration')}
			open={open}
			onTitleItemClick={toggleOpen}
			progress={completedSteps}
			progressTotal={totalSteps}
			startIcon={<SvgIcon fontSize="small" />}
			testIdTopic="xaa"
		>
			<SidebarMenuItem
				data-testid="xaa-select-idp-menu-item"
				active={Boolean(matchPath(ROUTES.selectXaa, location.pathname))}
				buttonBaseProps={{ onClick: () => nav(ROUTES.selectXaa) }}
				endIcon={currentXaaIdPData && <IdPChip idp={currentXaaIdPData} />}
				startIcon={
					<Check
						sx={{
							width: 20,
							visibility: currentXaaIdPData ? 'visible' : 'hidden'
						}}
						fontSize="small"
						color="primary"
					/>
				}
			>
				{intl.tr('xaaConfig.guide.selectIdP.pageTitle')}
			</SidebarMenuItem>
			{xaaGuideData?.guide.map((step, i) => (
				<Fade key={step.title} in style={{ transitionDelay: `${i * 75}ms` }}>
					<Stack>
						<SidebarMenuItem
							data-testid="guide-step-menu-item"
							active={
								guideType === GuideType.XAA &&
								Boolean(matchPath(ROUTES.guideStep, location.pathname)) &&
								xaaGuideStepNum === i + 1
							}
							buttonBaseProps={{ onClick: () => navigateToXaaStep(i + 1) }}
							startIcon={
								<Check
									sx={{
										width: 20,
										visibility: isStepCompleted(i + 1) ? 'visible' : 'hidden'
									}}
									fontSize="small"
									color="primary"
								/>
							}
						>
							{step.title}
						</SidebarMenuItem>
					</Stack>
				</Fade>
			))}
		</SidebarMenuSection>
	);
};

export default XAAMenuSection;
